import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '../lib/supabase';
import { Lighthouse, Visit } from '../types';
import LighthouseDetail from './LighthouseDetail';
import '../styles/Map.css';

interface Props {
  appUserId: string | null;
}

const visitedIcon = L.divIcon({
  className: '',
  html: '<div class="pin pin-visited"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const unvisitedIcon = L.divIcon({
  className: '',
  html: '<div class="pin pin-unvisited"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Removes the "Leaflet" software link from the map's credit line
function TidyAttribution() {
  const map = useMap();
  React.useEffect(() => {
    map.attributionControl.setPrefix(false);
  }, [map]);
  return null;
}

function MapPage({ appUserId }: Props) {
  const [lighthouses, setLighthouses] = useState<Lighthouse[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [tier, setTier] = useState('All');
  const [detail, setDetail] = useState<Lighthouse | null>(null);
  const [state, setState] = useState('All');

  useEffect(() => {
    supabase
      .from('lighthouses')
      .select('*')
      .then(({ data }) => setLighthouses(data || []));
  }, []);

  useEffect(() => {
    if (!appUserId) return;
    supabase
      .from('visits')
      .select('*')
      .eq('user_id', appUserId)
      .then(({ data }) => setVisits(data || []));
  }, [appUserId]);

  const visitedIds = useMemo(
    () => new Set(visits.filter((v) => v.verified).map((v) => v.lighthouse_id)),
    [visits]
  );

  const states = useMemo(
    () => ['All', ...Array.from(new Set(lighthouses.map((l) => l.state))).sort()],
    [lighthouses]
  );

  const shown = lighthouses.filter(
    (l) =>
      (tier === 'All' || l.passport_type === tier) &&
      (state === 'All' || l.state === state)
  );

  return (
    <div className="page map-page">
      <p
        style={{
          margin: '0 0 8px',
          fontSize: '16px',
          color: '#000000',
        }}
      >
        Tap any lighthouse for details and member photos.
      </p>
      <div className="map-filters">
        <select value={tier} onChange={(e) => setTier(e.target.value)}>
          <option>All</option>
          <option>Standard</option>
          <option>Extreme</option>
          <option>Heritage</option>
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)}>
          {states.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      <MapContainer
        center={[-28.5, 134]}
        zoom={4}
        className="leaflet-wrap"
        scrollWheelZoom
      >
        <TidyAttribution />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {shown.map((l) => (
          <Marker
            key={l.id}
            position={[Number(l.latitude), Number(l.longitude)]}
            icon={visitedIds.has(l.id) ? visitedIcon : unvisitedIcon}
          >
            <Popup>
              <strong>{l.name}</strong>
              <br />
              {l.passport_type} · {l.state}
              {l.year_established ? ` · est. ${l.year_established}` : ''}
              <br />
              {l.access ? `Access: ${l.access}` : ''}
              {l.access && l.status ? ' · ' : ''}
              {l.status || ''}
              <br />
              {visitedIds.has(l.id) ? 'Stamped' : 'Not visited yet'}
              <br />
              <a
                href={l.maps_url || `https://www.google.com/maps?q=${l.latitude},${l.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in Google Maps
              </a>
              <br />
              <button
                onClick={() => setDetail(l)}
                style={{
                  marginTop: '6px',
                  border: 'none',
                  background: '#1b2e4b',
                  color: '#ffffff',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Full details and photos
              </button>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {detail && (
        <LighthouseDetail lighthouse={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

export default MapPage;
